import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  FileText, Clock, CheckCircle2, DollarSign, Users, TrendingUp,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      setRecentPayments(response.data.recent_payments || []);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data dashboard (unauthorized).");
      } else {
        toast.error("Gagal memuat data dashboard");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statCards = stats ? [
    {
      title: "Total Tagihan",
      value: stats.total_bills,
      icon: FileText,
      color: "text-sky-600",
      bg: "bg-sky-100",
    },
    {
      title: "Belum Dibayar",
      value: stats.unpaid_bills,
      icon: AlertCircle,
      color: "text-red-600",
      bg: "bg-red-100",
    },
    {
      title: "Menunggu Verifikasi",
      value: stats.pending_payments,
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-100",
    },
    {
      title: "Lunas",
      value: stats.paid_bills,
      icon: CheckCircle2,
      color: "text-emerald-600",
      bg: "bg-emerald-100",
    },
    {
      title: "Total Pendapatan",
      value: formatCurrency(stats.total_revenue),
      icon: DollarSign,
      color: "text-sky-600",
      bg: "bg-sky-100",
      isLarge: true,
    },
    {
      title: "Pending Amount",
      value: formatCurrency(stats.pending_amount),
      icon: TrendingUp,
      color: "text-amber-600",
      bg: "bg-amber-100",
      isLarge: true,
    },
  ] : [];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      rejected: "status-rejected",
    };
    const labels = {
      pending: "Pending",
      confirmed: "Dikonfirmasi",
      rejected: "Ditolak",
    };
    return <span className={styles[status]}>{labels[status]}</span>;
  };

  return (
    <AdminLayout title="Dashboard">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="stats-grid">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="stat-card">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                          <p className={`${stat.isLarge ? "text-xl" : "text-2xl"} font-heading font-bold text-slate-800`}>
                            {stat.value}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Recent Payments */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading">Pembayaran Terbaru</CardTitle>
            </CardHeader>
            <CardContent>
              {recentPayments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Belum ada pembayaran
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Pelanggan</th>
                        <th>Periode</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Tanggal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPayments.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <div>
                              <p className="font-medium text-slate-800">{payment.nama}</p>
                              <p className="text-xs text-slate-500">{payment.no_samb}</p>
                            </div>
                          </td>
                          <td>{payment.bulan}</td>
                          <td className="font-medium">{formatCurrency(payment.total_bayar)}</td>
                          <td>{getStatusBadge(payment.status)}</td>
                          <td className="text-sm text-slate-500">{formatDate(payment.submitted_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AdminLayout>
  );
}
